import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { sanitizeRich } from '../../lib/sanitize';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Bot, Plus, Upload, Sparkles, Mail, FileSignature, Trash2, Target, TrendingUp, Users, Award, Loader2, Send, MessageCircle, ChevronRight, Pencil, Check, X } from 'lucide-react';

const PIPELINE_KEYS = [
    { key: 'nuovo', labelKey: 'pages.fv_stage_new', color: 'bg-[hsl(220_10%_85%)] text-[hsl(220_13%_15%)]' },
    { key: 'contattato', labelKey: 'pages.fv_stage_contacted', color: 'bg-[hsl(210_90%_88%)] text-[hsl(210_80%_25%)]' },
    { key: 'risposto', labelKey: 'pages.fv_stage_replied', color: 'bg-[hsl(45_100%_85%)] text-[hsl(35_80%_25%)]' },
    { key: 'preventivo_inviato', labelKey: 'pages.fv_stage_quote_sent', color: 'bg-[hsl(280_70%_88%)] text-[hsl(280_60%_25%)]' },
    { key: 'contratto_inviato', labelKey: 'pages.fv_stage_contract_sent', color: 'bg-[hsl(190_70%_86%)] text-[hsl(190_70%_22%)]' },
    { key: 'contratto_firmato', labelKey: 'pages.fv_stage_contract_signed', color: 'bg-[hsl(145_45%_86%)] text-[hsl(145_55%_22%)]' },
    { key: 'fattura_emessa', labelKey: 'pages.fv_stage_invoiced', color: 'bg-[hsl(30_90%_86%)] text-[hsl(25_80%_25%)]' },
    { key: 'pagata', labelKey: 'pages.fv_stage_paid', color: 'bg-[hsl(145_55%_75%)] text-[hsl(145_60%_18%)]' },
    { key: 'non_interessato', labelKey: 'pages.fv_stage_not_interested', color: 'bg-[hsl(0_50%_88%)] text-[hsl(0_60%_28%)]' },
];

const ACTIVITY_LABEL_KEYS = {
    qualified: 'pages.fv_act_qualified',
    email_sent: 'pages.fv_act_email_sent',
    update: 'pages.fv_act_update',
    contratto_generato: 'pages.fv_act_contract',
    opt_out: 'pages.fv_act_optout',
};

const stateColor = (s) => PIPELINE_KEYS.find(p => p.key === s)?.color || 'bg-[hsl(var(--muted))]';

// Hook to get translated pipeline + helpers
function usePipeline() {
    const { t } = useTranslation();
    const PIPELINE = PIPELINE_KEYS.map(p => ({ ...p, label: t(p.labelKey) }));
    const stateLabel = (s) => PIPELINE.find(p => p.key === s)?.label || s;
    const activityLabel = (type) => ACTIVITY_LABEL_KEYS[type] ? t(ACTIVITY_LABEL_KEYS[type]) : type;
    return { PIPELINE, stateLabel, stateColor, activityLabel };
}

export default function AutoPilot({ embedded = false }) {
    const { t } = useTranslation();
    const { PIPELINE, stateLabel } = usePipeline();
    const [tab, setTab] = useState('dashboard');
    const [dash, setDash] = useState(null);
    const [leads, setLeads] = useState([]);
    const [filter, setFilter] = useState('all');
    const [selected, setSelected] = useState(null);
    const [openNew, setOpenNew] = useState(false);
    const [openOutreach, setOpenOutreach] = useState(false);
    const [openContract, setOpenContract] = useState(false);
    const [activities, setActivities] = useState([]);
    const fileInputRef = useRef(null);

    const load = async () => {
        const [d, l, a] = await Promise.all([
            api.get('/autopilot/dashboard'),
            api.get('/autopilot/leads'),
            api.get('/autopilot/activities').catch(() => ({ data: [] })),
        ]);
        setDash(d.data);
        setLeads(l.data);
        setActivities(a.data || []);
    };
    useEffect(() => { load(); }, []);

    const filtered = filter === 'all' ? leads : leads.filter(l => l.stato === filter);

    return (
        <div data-testid="autopilot-page">
            {!embedded && (
                <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                    <div>
                        <div className="text-xs tracking-widest text-[hsl(var(--muted-foreground))]">PLUS+</div>
                        <h1 className="font-display text-4xl md:text-5xl flex items-center gap-3">
                            <Bot className="w-10 h-10 text-[hsl(var(--accent))]" /> {t('pages.fv_title')}
                        </h1>
                        <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('pages.fv_subtitle')}</p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <Button onClick={() => fileInputRef.current?.click()} variant="outline" className="gap-2" data-testid="autopilot-csv-button">
                            <Upload className="w-4 h-4" /> {t('pages.fv_csv_upload')}
                        </Button>
                        <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={async (e) => {
                            const file = e.target.files?.[0]; if (!file) return;
                            const fd = new FormData(); fd.append('file', file);
                            try {
                                const res = await api.post('/autopilot/leads/upload-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                                toast.success(t('pages.fv_csv_imported', { n: res.data.inserted }));
                                load();
                            } catch (err) { toast.error(t('pages.fv_csv_err')); }
                            e.target.value = '';
                        }} />
                        <Button onClick={() => setOpenNew(true)} className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold gap-2" data-testid="autopilot-new-lead">
                            <Plus className="w-4 h-4" /> {t('pages.fv_new_lead')}
                        </Button>
                    </div>
                </div>
            )}

            {embedded && (
                <div className="flex items-center justify-end mb-4 flex-wrap gap-2">
                    <Button onClick={() => fileInputRef.current?.click()} variant="outline" size="sm" className="gap-2" data-testid="autopilot-csv-button">
                        <Upload className="w-4 h-4" /> {t('pages.fv_csv_upload')}
                    </Button>
                    <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={async (e) => {
                        const file = e.target.files?.[0]; if (!file) return;
                        const fd = new FormData(); fd.append('file', file);
                        try {
                            const res = await api.post('/autopilot/leads/upload-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                            toast.success(t('pages.fv_csv_imported', { n: res.data.inserted }));
                            load();
                        } catch (err) { toast.error(t('pages.fv_csv_err')); }
                        e.target.value = '';
                    }} />
                    <Button onClick={() => setOpenNew(true)} size="sm" className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold gap-2" data-testid="autopilot-new-lead">
                        <Plus className="w-4 h-4" /> {t('pages.fv_new_lead')}
                    </Button>
                </div>
            )}

            {/* Activity Feed — Cross-agent timeline */}
            {activities && activities.length > 0 && (
                <Card className="p-4 mb-5 bg-[hsl(var(--muted))]/30" data-testid="autopilot-activity-feed">
                    <div className="flex items-center gap-2 mb-3">
                        <MessageCircle className="w-4 h-4 text-[hsl(var(--accent))]" />
                        <h3 className="font-semibold text-sm">{t('pages.fv_activity_feed') || 'Team AI · Attività recenti'}</h3>
                        <Badge variant="outline" className="text-[10px]">{activities.length}</Badge>
                    </div>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {activities.slice(0, 12).map((a) => {
                            const dt = a.at ? new Date(a.at) : null;
                            const dtStr = dt ? dt.toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
                            return (
                                <div key={a.id || (a.at + a.message)} className="flex items-start gap-2 text-xs py-1 border-b border-[hsl(var(--border))]/40 last:border-0" data-testid={`activity-${a.agent}`}>
                                    <span className="text-base leading-none">{a.icon || '🤖'}</span>
                                    <div className="flex-1 min-w-0">
                                        <span className="font-semibold text-[hsl(var(--accent-foreground))]">{a.agent_label || a.agent}</span>
                                        <span className="mx-1 text-[hsl(var(--muted-foreground))]">·</span>
                                        <span>{a.message}</span>
                                    </div>
                                    <span className="text-[hsl(var(--muted-foreground))] whitespace-nowrap">{dtStr}</span>
                                </div>
                            );
                        })}
                    </div>
                </Card>
            )}

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="dashboard" data-testid="autopilot-tab-dashboard">{t('pages.fv_tab_dashboard')}</TabsTrigger>
                    <TabsTrigger value="leads" data-testid="autopilot-tab-leads">{t('pages.fv_tab_leads')} ({leads.length})</TabsTrigger>
                    <TabsTrigger value="pipeline" data-testid="autopilot-tab-pipeline">{t('pages.fv_tab_pipeline')}</TabsTrigger>
                </TabsList>

                <TabsContent value="dashboard">
                    {dash && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                            <Card className="p-5"><div className="flex items-center justify-between mb-1"><div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.fv_kpi_total')}</div><Users className="w-4 h-4 text-[hsl(var(--accent))]" /></div><div className="font-display text-4xl tabular-nums" data-testid="kpi-lead-totali">{dash.total_leads}</div></Card>
                            <Card className="p-5"><div className="flex items-center justify-between mb-1"><div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.fv_kpi_qualified')}</div><Target className="w-4 h-4 text-[hsl(var(--accent))]" /></div><div className="font-display text-4xl tabular-nums">{dash.qualified}</div></Card>
                            <Card className="p-5"><div className="flex items-center justify-between mb-1"><div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.fv_kpi_contracts')}</div><Award className="w-4 h-4 text-[hsl(var(--accent))]" /></div><div className="font-display text-4xl tabular-nums">{dash.contratti}</div></Card>
                            <Card className="p-5"><div className="flex items-center justify-between mb-1"><div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.fv_kpi_conv')}</div><TrendingUp className="w-4 h-4 text-[hsl(var(--accent))]" /></div><div className="font-display text-4xl tabular-nums">{dash.conversion_rate_pct}%</div></Card>
                        </div>
                    )}
                    {dash?.top_leads?.length > 0 && (
                        <Card className="p-5 mt-5">
                            <h3 className="font-display text-xl mb-3">{t('pages.fv_top_leads')}</h3>
                            <div className="space-y-2">
                                {dash.top_leads.map(l => (
                                    <div key={l.id} className="flex items-center justify-between p-3 rounded-lg border hover:border-[hsl(var(--accent))] cursor-pointer" onClick={() => { setSelected(l); setTab('leads'); }}>
                                        <div>
                                            <div className="font-semibold">{l.ragione_sociale}</div>
                                            <div className="text-xs text-[hsl(var(--muted-foreground))]">{l.settore} · {l.citta}</div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Badge className={stateColor(l.stato)}>{stateLabel(l.stato)}</Badge>
                                            <div className="font-display text-2xl tabular-nums">{l.score}<span className="text-sm">/100</span></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    )}
                </TabsContent>

                <TabsContent value="leads">
                    <div className="flex gap-2 mb-4 flex-wrap mt-4">
                        <button onClick={() => setFilter('all')} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${filter === 'all' ? 'bg-[hsl(220_13%_10%)] text-white' : 'bg-white'}`}>{t('common.all')}</button>
                        {PIPELINE.map(p => (
                            <button key={p.key} onClick={() => setFilter(p.key)} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${filter === p.key ? 'bg-[hsl(220_13%_10%)] text-white' : 'bg-white'}`} data-testid={`autopilot-filter-${p.key}`}>{p.label}</button>
                        ))}
                    </div>
                    <Card className="overflow-x-auto">
                        {filtered.length === 0 ? (
                            <div className="p-10 text-center text-[hsl(var(--muted-foreground))]">
                                <Bot className="w-12 h-12 mx-auto mb-3" />
                                <p>{t('pages.fv_empty_leads')}</p>
                            </div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead className="bg-[hsl(var(--muted))]"><tr className="text-left">
                                    <th className="p-3">{t('pages.fv_col_company')}</th><th className="p-3 hidden md:table-cell">{t('pages.fv_col_sector')}</th><th className="p-3 hidden md:table-cell">{t('pages.fv_col_city')}</th><th className="p-3">{t('pages.fv_col_score')}</th><th className="p-3">{t('pages.fv_col_status')}</th><th className="p-3"></th>
                                </tr></thead>
                                <tbody>
                                    {filtered.map(l => (
                                        <tr key={l.id} className="border-t hover:bg-[hsl(var(--muted))]/30" data-testid={`autopilot-lead-${l.id}`}>
                                            <td className="p-3"><div className="font-semibold">{l.ragione_sociale}</div><div className="text-xs text-[hsl(var(--muted-foreground))]">{l.referente || '—'} · {l.email || 'no email'}</div></td>
                                            <td className="p-3 hidden md:table-cell text-xs">{l.settore || '—'}</td>
                                            <td className="p-3 hidden md:table-cell text-xs">{l.citta || '—'}</td>
                                            <td className="p-3 font-display text-lg tabular-nums">{l.score || 0}</td>
                                            <td className="p-3"><Badge className={stateColor(l.stato)}>{stateLabel(l.stato)}</Badge></td>
                                            <td className="p-3 text-right"><Button size="sm" variant="ghost" onClick={() => setSelected(l)} className="gap-1">{t('pages.fv_action_manage')} <ChevronRight className="w-3 h-3" /></Button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="pipeline">
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-2 mt-4">
                        {PIPELINE.map(p => (
                            <div key={p.key} className={`rounded-lg p-3 ${p.color}`} data-testid={`pipeline-col-${p.key}`}>
                                <div className="text-xs font-semibold mb-1">{p.label}</div>
                                <div className="font-display text-3xl tabular-nums">{dash?.pipeline?.[p.key] || 0}</div>
                            </div>
                        ))}
                    </div>
                    <Card className="p-5 mt-5">
                        <h3 className="font-display text-xl mb-3">📊 FUNNEL DI CONVERSIONE</h3>
                        {PIPELINE.slice(0, 8).map((p, i) => {
                            const n = dash?.pipeline?.[p.key] || 0;
                            const max = Math.max(...PIPELINE.slice(0, 8).map(x => dash?.pipeline?.[x.key] || 0), 1);
                            return (
                                <div key={p.key} className="flex items-center gap-3 mb-2">
                                    <div className="w-32 text-sm">{p.label}</div>
                                    <div className="flex-1 bg-[hsl(var(--muted))] rounded h-6 relative overflow-hidden">
                                        <div className={`absolute inset-y-0 left-0 ${p.color}`} style={{ width: `${(n / max) * 100}%` }} />
                                        <span className="absolute right-2 top-0.5 text-xs font-semibold">{n}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </Card>
                </TabsContent>
            </Tabs>

            <NewLeadDialog open={openNew} onOpenChange={setOpenNew} onCreated={() => { load(); setOpenNew(false); }} />
            <LeadDetailDialog lead={selected} onClose={() => setSelected(null)} onChanged={load} onOutreach={() => setOpenOutreach(true)} onContract={() => setOpenContract(true)} />
            <OutreachDialog open={openOutreach} onOpenChange={setOpenOutreach} lead={selected} onSent={() => { load(); setOpenOutreach(false); }} />
            <ContrattoDialog open={openContract} onOpenChange={setOpenContract} lead={selected} onGenerated={() => { load(); setOpenContract(false); }} />
        </div>
    );
}


function NewLeadDialog({ open, onOpenChange, onCreated }) {
    const { t } = useTranslation();
    const [f, setF] = useState({ ragione_sociale: '', referente: '', email: '', telefono: '', settore: '', citta: '', sito_web: '', note: '' });
    const submit = async () => {
        if (!f.ragione_sociale) { toast.error(t('pages.fv_no_company')); return; }
        try {
            await api.post('/autopilot/leads', { ...f, email: f.email || null });
            toast.success(t('pages.fv_lead_created'));
            setF({ ragione_sociale: '', referente: '', email: '', telefono: '', settore: '', citta: '', sito_web: '', note: '' });
            onCreated();
        } catch (e) { toast.error(e.response?.data?.detail || t('common.generic_error')); }
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl">
                <DialogHeader><DialogTitle className="font-display text-2xl">{t('pages.fv_new_lead_title')}</DialogTitle></DialogHeader>
                <div className="grid sm:grid-cols-2 gap-3 mt-2">
                    <div className="sm:col-span-2"><Label>{t('pages.fv_field_company')}</Label><Input value={f.ragione_sociale} onChange={(e) => setF({...f, ragione_sociale: e.target.value})} data-testid="new-lead-rs" /></div>
                    <div><Label>{t('pages.fv_field_contact')}</Label><Input value={f.referente} onChange={(e) => setF({...f, referente: e.target.value})} /></div>
                    <div><Label>{t('common.email')}</Label><Input type="email" value={f.email} onChange={(e) => setF({...f, email: e.target.value})} data-testid="new-lead-email" /></div>
                    <div><Label>{t('pages.fv_field_phone')}</Label><Input value={f.telefono} onChange={(e) => setF({...f, telefono: e.target.value})} /></div>
                    <div><Label>{t('pages.fv_field_sector')}</Label><Input value={f.settore} onChange={(e) => setF({...f, settore: e.target.value})} placeholder={t('pages.fv_field_sector_ph')} /></div>
                    <div><Label>{t('pages.fv_field_city')}</Label><Input value={f.citta} onChange={(e) => setF({...f, citta: e.target.value})} /></div>
                    <div><Label>{t('pages.fv_field_website')}</Label><Input value={f.sito_web} onChange={(e) => setF({...f, sito_web: e.target.value})} /></div>
                    <div className="sm:col-span-2"><Label>{t('pages.fv_field_notes')}</Label><Textarea rows={2} value={f.note} onChange={(e) => setF({...f, note: e.target.value})} /></div>
                </div>
                <Button onClick={submit} className="w-full mt-3 bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] h-11 font-bold" data-testid="new-lead-submit">{t('pages.fv_submit_lead')}</Button>
            </DialogContent>
        </Dialog>
    );
}


function LeadDetailDialog({ lead, onClose, onChanged, onOutreach, onContract }) {
    const { t } = useTranslation();
    const { PIPELINE, stateLabel, activityLabel } = usePipeline();
    const [qualifyOpen, setQualifyOpen] = useState(false);
    const [qualifyForm, setQualifyForm] = useState({ settore_attivita: '', zona_geografica: '' });
    const [qualifying, setQualifying] = useState(false);
    if (!lead) return null;

    const setStato = async (s) => {
        await api.put(`/autopilot/leads/${lead.id}`, { stato: s });
        toast.success(t('pages.fv_moved_to', { stage: stateLabel(s) }));
        onChanged();
        onClose();
    };
    const remove = async () => {
        if (!window.confirm(t('pages.fv_confirm_delete_lead'))) return;
        await api.delete(`/autopilot/leads/${lead.id}`);
        toast.success(t('pages.fv_lead_deleted'));
        onChanged();
        onClose();
    };
    const qualify = async () => {
        if (!qualifyForm.settore_attivita) { toast.error(t('pages.fv_qualify_no_sector')); return; }
        setQualifying(true);
        try {
            const res = await api.post(`/autopilot/leads/${lead.id}/qualify`, qualifyForm);
            const status = res.data.qualified ? t('pages.fv_qualified_ok') : t('pages.fv_qualified_ko');
            toast.success(t('pages.fv_qualify_score', { score: res.data.score, status }));
            setQualifyOpen(false);
            onChanged();
        } catch (e) { toast.error(t('common.generic_error')); }
        finally { setQualifying(false); }
    };

    return (
        <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-display text-2xl">{lead.ragione_sociale}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3 text-sm mt-2 pb-3 border-b">
                    <div><div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.fv_field_contact')}</div>{lead.referente || '—'}</div>
                    <div><div className="text-xs text-[hsl(var(--muted-foreground))]">{t('common.email')}</div>{lead.email || '—'}</div>
                    <div><div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.fv_field_phone')}</div>{lead.telefono || '—'}</div>
                    <div><div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.fv_field_sector')}</div>{lead.settore || '—'}</div>
                    <div><div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.fv_field_city')}</div>{lead.citta || '—'}</div>
                    <div><div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.fv_field_website')}</div>{lead.sito_web ? <a href={lead.sito_web} target="_blank" rel="noreferrer" className="underline">{lead.sito_web}</a> : '—'}</div>
                </div>

                <div className="flex items-center justify-between mt-3">
                    <div>
                        <Badge className={stateColor(lead.stato)}>{stateLabel(lead.stato)}</Badge>
                        {lead.score > 0 && <Badge className="ml-2 bg-[hsl(var(--accent))]/20 text-[hsl(220_13%_10%)]">{t('pages.fv_col_score')} {lead.score}/100</Badge>}
                    </div>
                </div>

                {lead.qualifica_ai && (
                    <div className="mt-3 p-3 rounded-lg bg-[hsl(var(--accent))]/5 border border-[hsl(var(--accent))]/30 text-sm">
                        <div className="font-semibold mb-1">{t('pages.fv_lead_qualifica_ai')}</div>
                        <p>{lead.qualifica_ai.motivazione}</p>
                        <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">{t('pages.fv_lead_next_action')} <strong>{lead.qualifica_ai.next_action}</strong></p>
                    </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-4">
                    <Button onClick={() => setQualifyOpen(true)} variant="outline" className="gap-2" data-testid="lead-qualify-button"><Sparkles className="w-4 h-4 text-[hsl(var(--accent))]" /> {t('pages.fv_action_qualify_ai')}</Button>
                    <Button onClick={onOutreach} disabled={!lead.email} className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] gap-2 font-bold" data-testid="lead-outreach-button"><Mail className="w-4 h-4" /> {t('pages.fv_action_outreach')}</Button>
                    <Button onClick={onContract} variant="outline" className="gap-2" data-testid="lead-contract-button"><FileSignature className="w-4 h-4 text-[hsl(var(--accent))]" /> {t('pages.fv_action_contract')}</Button>
                </div>

                <div className="mt-4">
                    <Label className="text-xs">{t('pages.fv_move_to')}</Label>
                    <Select onValueChange={setStato}>
                        <SelectTrigger className="mt-1"><SelectValue placeholder={t('pages.fv_move_select')} /></SelectTrigger>
                        <SelectContent>{PIPELINE.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
                    </Select>
                </div>

                {lead.activities?.length > 0 && (
                    <div className="mt-4 pt-3 border-t">
                        <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))] mb-2">{t('pages.fv_history')}</div>
                        <ul className="text-xs space-y-1 max-h-32 overflow-y-auto">
                            {lead.activities.slice(-10).reverse().map((a, i) => (
                                <li key={i} className="flex justify-between"><span>{activityLabel(a.type)}</span><span className="text-[hsl(var(--muted-foreground))]">{new Date(a.at).toLocaleString()}</span></li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="flex justify-between mt-4 pt-3 border-t">
                    <Button variant="ghost" onClick={remove} className="text-[hsl(var(--destructive))]"><Trash2 className="w-4 h-4 mr-1" /> {t('common.delete')}</Button>
                </div>

                <Dialog open={qualifyOpen} onOpenChange={setQualifyOpen}>
                    <DialogContent>
                        <DialogHeader><DialogTitle className="font-display text-2xl">{t('pages.fv_qualify_title')}</DialogTitle></DialogHeader>
                        <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('pages.fv_qualify_desc')}</p>
                        <div className="space-y-3 mt-2">
                            <div><Label>{t('pages.fv_qualify_services')}</Label><Textarea rows={2} value={qualifyForm.settore_attivita} onChange={(e) => setQualifyForm({...qualifyForm, settore_attivita: e.target.value})} placeholder="Es. Ristrutturazioni e impianti idraulici" data-testid="qualify-settore" /></div>
                            <div><Label>{t('pages.fv_qualify_zone')}</Label><Input value={qualifyForm.zona_geografica} onChange={(e) => setQualifyForm({...qualifyForm, zona_geografica: e.target.value})} placeholder="Es. Lombardia" /></div>
                            <Button onClick={qualify} disabled={qualifying} className="w-full bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] h-11 font-bold" data-testid="qualify-submit">
                                {qualifying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('pages.fv_qualify_analyzing')}</> : <><Sparkles className="w-4 h-4 mr-2" /> {t('pages.fv_qualify_btn')}</>}
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </DialogContent>
        </Dialog>
    );
}


function OutreachDialog({ open, onOpenChange, lead, onSent }) {
    const { t } = useTranslation();
    const [form, setForm] = useState({ tono: 'professionale', proposta_value: '' });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    // --- Edit-before-send state ---
    const [editing, setEditing] = useState(false);
    const [draftOggetto, setDraftOggetto] = useState('');
    const [draftBody, setDraftBody] = useState('');

    // Convert HTML to plain text for editing (strip tags, decode &nbsp;, keep paragraph breaks).
    const htmlToPlain = (html) => {
        if (!html) return '';
        const tmp = document.createElement('div');
        tmp.innerHTML = html
            .replace(/<\s*br\s*\/?>/gi, '\n')
            .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
            .replace(/<li[^>]*>/gi, '• ');
        return (tmp.textContent || tmp.innerText || '')
            .replace(/\u00a0/g, ' ')
            .replace(/\n{3,}/g, '\n\n')
            .trim();
    };
    // Convert plain text back to safe HTML (escape + paragraphs from blank lines).
    const plainToHtml = (txt) => {
        if (!txt) return '';
        const esc = txt
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
        return esc
            .split(/\n{2,}/)
            .map(par => `<p>${par.replace(/\n/g, '<br>')}</p>`) 
            .join('');
    };

    const generate = async () => {
        if (!lead) return;
        setLoading(true);
        try {
            const res = await api.post('/autopilot/outreach/generate', { lead_id: lead.id, ...form });
            setData(res.data);
            setEditing(false);
        } catch (e) { toast.error(t('common.generic_error')); }
        finally { setLoading(false); }
    };
    const send = async () => {
        if (!data) return;
        setSending(true);
        try {
            await api.post('/autopilot/outreach/send', { lead_id: lead.id, oggetto: data.oggetto, email_html: data.email_html });
            toast.success(t('pages.fv_outreach_sent'));
            setData(null);
            setEditing(false);
            onSent();
        } catch (e) { toast.error(e.response?.data?.detail || t('common.generic_error')); }
        finally { setSending(false); }
    };
    const startEdit = () => {
        if (!data) return;
        setDraftOggetto(data.oggetto || '');
        setDraftBody(htmlToPlain(data.email_html || ''));
        setEditing(true);
    };
    const cancelEdit = () => {
        setEditing(false);
    };
    const saveEdit = () => {
        const cleanSubject = (draftOggetto || '').trim();
        if (!cleanSubject) {
            toast.error(t('pages.fv_outreach_subject_required') || 'Oggetto obbligatorio');
            return;
        }
        const newHtml = plainToHtml(draftBody);
        setData({ ...data, oggetto: cleanSubject, email_html: newHtml });
        setEditing(false);
        toast.success(t('pages.fv_outreach_changes_saved') || 'Modifiche salvate');
    };
    const copy = (s) => { navigator.clipboard.writeText(s); toast.success(t('common.copied')); };

    useEffect(() => { if (!open) { setData(null); setEditing(false); } }, [open]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Sparkles className="w-5 h-5 text-[hsl(var(--accent))]" /> {t('pages.fv_outreach_title')}</DialogTitle></DialogHeader>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('pages.fv_outreach_desc', { company: lead?.ragione_sociale })}</p>
                <div className="grid gap-3 mt-3">
                    <div>
                        <Label>{t('pages.fv_outreach_tone')}</Label>
                        <Select value={form.tono} onValueChange={(v) => setForm({...form, tono: v})}>
                            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="professionale">{t('pages.fv_outreach_tone_pro')}</SelectItem>
                                <SelectItem value="amichevole">{t('pages.fv_outreach_tone_friendly')}</SelectItem>
                                <SelectItem value="diretto">{t('pages.fv_outreach_tone_direct')}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>{t('pages.fv_outreach_value')}</Label>
                        <Textarea rows={2} value={form.proposta_value} onChange={(e) => setForm({...form, proposta_value: e.target.value})} placeholder={t('pages.fv_outreach_value_ph')} />
                    </div>
                    <Button onClick={generate} disabled={loading} className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold" data-testid="outreach-generate">
                        {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('common.generating')}</> : <><Sparkles className="w-4 h-4 mr-2" /> {t('pages.fv_outreach_generate')}</>}
                    </Button>
                </div>

                {data && (
                    <Tabs defaultValue="email" className="mt-4">
                        <TabsList>
                            <TabsTrigger value="email"><Mail className="w-4 h-4 mr-1" /> {t('common.email')}</TabsTrigger>
                            <TabsTrigger value="whatsapp"><MessageCircle className="w-4 h-4 mr-1" /> WhatsApp</TabsTrigger>
                        </TabsList>
                        <TabsContent value="email">
                            <div className="border rounded-lg overflow-hidden mt-2">
                                {editing ? (
                                    <div className="p-3 space-y-3 bg-[hsl(var(--muted))]/30">
                                        <div>
                                            <Label className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{t('pages.fv_outreach_subject')}</Label>
                                            <Input
                                                value={draftOggetto}
                                                onChange={(e) => setDraftOggetto(e.target.value)}
                                                className="mt-1 h-11 font-semibold"
                                                data-testid="outreach-edit-subject"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{t('pages.fv_outreach_body_label') || 'Corpo email'}</Label>
                                            <Textarea
                                                rows={12}
                                                value={draftBody}
                                                onChange={(e) => setDraftBody(e.target.value)}
                                                className="mt-1 font-mono text-sm leading-relaxed"
                                                data-testid="outreach-edit-body"
                                            />
                                            <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">
                                                {t('pages.fv_outreach_edit_hint') || 'Una riga vuota = nuovo paragrafo.'}
                                            </p>
                                        </div>
                                        <div className="flex gap-2 justify-end pt-1">
                                            <Button variant="outline" size="sm" onClick={cancelEdit} data-testid="outreach-edit-cancel">
                                                <X className="w-4 h-4 mr-1" /> {t('common.cancel') || 'Annulla'}
                                            </Button>
                                            <Button size="sm" onClick={saveEdit} className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold" data-testid="outreach-edit-save">
                                                <Check className="w-4 h-4 mr-1" /> {t('common.save') || 'Salva modifiche'}
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="bg-[hsl(var(--muted))] p-3 border-b flex items-start justify-between gap-3">
                                            <div className="min-w-0 flex-1">
                                                <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.fv_outreach_subject')}</div>
                                                <div className="font-semibold break-words">{data.oggetto}</div>
                                            </div>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={startEdit}
                                                className="shrink-0 gap-1"
                                                data-testid="outreach-edit-start"
                                            >
                                                <Pencil className="w-3.5 h-3.5" /> {t('common.edit') || 'Modifica'}
                                            </Button>
                                        </div>
                                        <div className="p-3 prose prose-sm max-w-none" dangerouslySetInnerHTML={sanitizeRich(data.email_html)} />
                                    </>
                                )}
                            </div>
                            <Button onClick={send} disabled={sending || editing || !lead?.email} className="w-full mt-3 bg-[hsl(145_55%_34%)] text-white h-11 disabled:opacity-60" data-testid="outreach-send">
                                {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('common.sending')}</> : <><Send className="w-4 h-4 mr-2" /> {t('pages.fv_outreach_send_to', { email: lead?.email || 'lead' })}</>}
                            </Button>
                            {editing && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2 text-center">{t('pages.fv_outreach_save_first') || 'Salva o annulla le modifiche per inviare.'}</p>}
                            {!lead?.email && !editing && <p className="text-xs text-[hsl(var(--destructive))] mt-2">{t('pages.fv_outreach_no_email')}</p>}
                        </TabsContent>
                        <TabsContent value="whatsapp">
                            <div className="border rounded-lg p-4 bg-[hsl(145_25%_96%)] mt-2">
                                <div className="flex justify-end mb-1"><Button size="sm" variant="ghost" onClick={() => copy(data.whatsapp_alternativo)}>📋 {t('common.copy')}</Button></div>
                                <p className="text-sm whitespace-pre-wrap">{data.whatsapp_alternativo}</p>
                            </div>
                        </TabsContent>
                    </Tabs>
                )}
            </DialogContent>
        </Dialog>
    );
}


function ContrattoDialog({ open, onOpenChange, lead, onGenerated }) {
    const { t } = useTranslation();
    const [form, setForm] = useState({ descrizione_lavori: '', importo: '', durata_giorni: 60 });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const generate = async () => {
        if (!lead) return;
        if (!form.descrizione_lavori) { toast.error(t('pages.fv_contract_no_desc')); return; }
        setLoading(true);
        try {
            const res = await api.post('/autopilot/contratti/generate', {
                lead_id: lead.id,
                descrizione_lavori: form.descrizione_lavori,
                importo: form.importo ? parseFloat(form.importo) : null,
                durata_giorni: parseInt(form.durata_giorni) || 60,
            });
            setData(res.data);
            toast.success(t('pages.fv_contract_generated'));
            onGenerated();
        } catch (e) { toast.error(t('common.generic_error')); }
        finally { setLoading(false); }
    };

    useEffect(() => { if (!open) setData(null); }, [open]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><FileSignature className="w-5 h-5 text-[hsl(var(--accent))]" /> {t('pages.fv_contract_title')}</DialogTitle></DialogHeader>
                {!data ? (
                    <div className="space-y-3 mt-2">
                        <div><Label>{t('pages.fv_contract_desc')}</Label><Textarea rows={3} value={form.descrizione_lavori} onChange={(e) => setForm({...form, descrizione_lavori: e.target.value})} placeholder={t('pages.fv_contract_desc_ph')} data-testid="contract-desc" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>{t('pages.fv_contract_amount')}</Label><Input type="number" value={form.importo} onChange={(e) => setForm({...form, importo: e.target.value})} placeholder="0.00" /></div>
                            <div><Label>{t('pages.fv_contract_duration')}</Label><Input type="number" value={form.durata_giorni} onChange={(e) => setForm({...form, durata_giorni: e.target.value})} /></div>
                        </div>
                        <Button onClick={generate} disabled={loading} className="w-full bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] h-11 font-bold" data-testid="contract-generate">
                            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('pages.fv_contract_generating')}</> : <><Sparkles className="w-4 h-4 mr-2" /> {t('pages.fv_contract_generate')}</>}
                        </Button>
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.fv_contract_legal_warn')}</p>
                    </div>
                ) : (
                    <div className="mt-2 space-y-3">
                        <h2 className="font-display text-2xl">{data.contenuto?.titolo}</h2>
                        <p className="text-sm whitespace-pre-wrap">{data.contenuto?.premesse}</p>
                        {(data.contenuto?.articoli || []).map((a, i) => (
                            <div key={i} className="border-l-4 border-[hsl(var(--accent))]/30 pl-3">
                                <div className="font-semibold text-sm">Art. {a.numero} - {a.titolo}</div>
                                <p className="text-sm text-[hsl(var(--muted-foreground))] whitespace-pre-wrap">{a.testo}</p>
                            </div>
                        ))}
                        <div className="p-3 bg-[hsl(45_100%_92%)] rounded text-xs">⚠ {data.contenuto?.note_finali}</div>
                        <Button onClick={() => window.print()} variant="outline" className="w-full">{t('pages.fv_contract_print_pdf')}</Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
